import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api/client';
import { CountdownOverlay } from '../components/CountdownOverlay';
import { DezoomDots } from '../components/DezoomDots';
import { GuessForm } from '../components/GuessForm';
import { NotificationOnboardingModal } from '../components/NotificationOnboardingModal';
import { PlayZoomStage } from '../components/PlayZoomStage';
import { ZoomImage } from '../components/ZoomImage';
import { useGameCountdown } from '../hooks/useGameCountdown';
import { useAuth } from '../hooks/useAuth';
import { preloadImages } from '../utils/imageCache';
import { currentZoomImageUrl, mergeZoomLevel } from '../utils/zoomLevels';
import { trackPlay } from '../utils/hub-play';

interface TodayResponse {
  puzzle: { id: string; puzzleNumber: number };
  imageUrl: string;
  zoomLevels: { level: number; imageUrl: string }[];
  currentZoomLevel: number;
  run: {
    id: string;
    status: string;
    dezoomsUsed?: number;
    currentZoomLevel?: number;
    startedAt?: string;
  } | null;
  hasOfficialRun: boolean;
}

interface GuessResponse {
  correct: boolean;
  status: string;
  dezoomsUsed: number;
  currentZoomLevel: number;
  score: number | null;
  shareText: string | null;
  durationSec: number | null;
  puzzleId?: string;
  answer?: string;
  answerEn?: string;
  imageUrl?: string;
}

const NOTIFY_ONBOARDING_KEY = 'dezoom_notify_onboarding';

export function PlayPage() {
  const navigate = useNavigate();
  const { user, loginGuest, loading: authLoading } = useAuth();
  const [today, setToday] = useState<TodayResponse | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const [zoomLevels, setZoomLevels] = useState<{ level: number; imageUrl: string }[]>([]);
  const [dezooms, setDezooms] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [timerSec, setTimerSec] = useState(0);
  const [skipCountdown, setSkipCountdown] = useState(false);
  const [initDone, setInitDone] = useState(false);
  const [showNotifyOnboarding, setShowNotifyOnboarding] = useState(false);

  const startRun = useCallback(async () => {
    if (runId) return;
    const res = await api<{ runId: string; startedAt: string }>('/runs/start', { method: 'POST' });
    setRunId(res.runId);
    trackPlay({ gameSlug: 'dezoom', gameKind: 'original' });
    const t = new Date(res.startedAt).getTime();
    setStartedAt(t);
    setTimerSec(0);
  }, [runId]);

  const { display, revealed, ready } = useGameCountdown({
    enabled: initDone && !skipCountdown,
    skip: initDone && skipCountdown,
    onGo: startRun,
  });

  const init = useCallback(async () => {
    if (!getToken() && !user) {
      await loginGuest();
    }
    const data = await api<TodayResponse>('/puzzles/today');
    const levels = data.zoomLevels || [];
    const level = data.run?.currentZoomLevel ?? data.currentZoomLevel;
    setToday(data);
    setZoomLevels(levels);
    setZoomLevel(level);
    const preloadUrl = currentZoomImageUrl(levels, level, data.imageUrl);
    if (preloadUrl) preloadImages([preloadUrl]);

    if (data.run) {
      setDezooms(data.run.dezoomsUsed ?? 0);
      if (data.run.status !== 'in_progress') {
        navigate('/result', { state: { fromRun: data.run.id } });
        return;
      }
      setRunId(data.run.id);
      if (data.run.startedAt) {
        setStartedAt(new Date(data.run.startedAt).getTime());
      }
      setSkipCountdown(true);
    } else {
      setSkipCountdown(false);
    }
    setInitDone(true);
  }, [user, loginGuest, navigate]);

  useEffect(() => {
    if (authLoading) return;
    init().catch((e) => setError(e.message));
  }, [authLoading, init]);

  useEffect(() => {
    if (authLoading || !user || user.isGuest) return;
    if (sessionStorage.getItem(NOTIFY_ONBOARDING_KEY) === '1') {
      setShowNotifyOnboarding(true);
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => {
      setTimerSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const handleGuess = async (guess: string): Promise<boolean | void> => {
    if (!runId) return;
    setBusy(true);
    setError('');
    try {
      const res = await api<GuessResponse>(`/runs/${runId}/guess`, {
        method: 'POST',
        body: JSON.stringify({ guess }),
      });

      setDezooms(res.dezoomsUsed);
      setZoomLevel(res.currentZoomLevel);
      if (res.imageUrl) {
        setZoomLevels((prev) => mergeZoomLevel(prev, res.currentZoomLevel, res.imageUrl));
        preloadImages([res.imageUrl]);
      }

      if (res.status !== 'in_progress') {
        navigate('/result', {
          state: {
            won: res.status === 'won',
            score: res.score ?? 0,
            dezoomsUsed: res.dezoomsUsed,
            durationSec: res.durationSec ?? 0,
            shareText: res.shareText,
            puzzleNumber: today?.puzzle.puzzleNumber ?? 0,
            puzzleId: today?.puzzle.id,
            isArchive: false,
            zoom0ImageUrl: zoomLevels.find((z) => z.level === 0)?.imageUrl ?? zoomLevels[0]?.imageUrl,
            answer: res.answer,
            answerEn: res.answerEn,
            imageUrl: res.imageUrl,
          },
        });
        return;
      }
      return res.correct;
    } catch (e: unknown) {
      const err = e as { data?: { error?: string } };
      setError(err.data?.error || 'Erreur');
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (!today) {
    return <main className="page play"><p className="loading">{error || 'Chargement…'}</p></main>;
  }

  return (
    <main className="page play">
      {showNotifyOnboarding && (
        <NotificationOnboardingModal
          onClose={() => {
            sessionStorage.removeItem(NOTIFY_ONBOARDING_KEY);
            setShowNotifyOnboarding(false);
          }}
        />
      )}
      <header className="play-header">
        <div className="play-header-left">
          <span className="puzzle-num">#{today.puzzle.puzzleNumber}</span>
          {startedAt != null && (
            <span className="play-timer" aria-live="polite">{formatTimer(timerSec)}</span>
          )}
        </div>
        <DezoomDots used={dezooms} />
      </header>

      <PlayZoomStage revealed={revealed} countdown={<CountdownOverlay value={display} />}>
        <ZoomImage levels={zoomLevels} activeLevel={zoomLevel} />
      </PlayZoomStage>

      <footer className="play-footer">
        {error && <p className="play-error" role="alert">{error}</p>}
        <GuessForm onSubmit={handleGuess} disabled={busy || !ready || !runId} />
      </footer>
    </main>
  );
}
